/**
 * Default Interview Rounds Utility
 *
 * Centralized logic for ensuring default interview rounds exist.
 * This ensures consistency across all places where default rounds are initialized.
 */

import { InterviewRoundState } from "./formTransformers";
import { DEFAULT_PASS_PERCENTAGE } from "../../../utils/constants";

export const OVERALL_ASSESSMENT_DEFAULT_PROMPT =
  "Consider all the listed job requirements as the foundation for this interview round. Use these requirements to generate a balanced set of questions that comprehensively evaluate the candidate's suitability for the role. The questions should: - Cover all key requirements (both technical and non-technical). - Assess core skills, problem-solving ability, and domain knowledge relevant to the role. - Include a mix of conceptual understanding, practical application, and scenario-based evaluation. - Be phrased in a way that allows clear assessment of the candidate's competence and alignment with the role. - Be distributed across difficulty levels (easy, medium, hard) to gradually test the candidate's knowledge and skills. Ensure that the generated questions collectively give the interviewer a complete picture of the candidate's match against the job requirements.";

const isOverallAssessmentRound = (round: InterviewRoundState): boolean =>
  round.title?.toLowerCase() === "overall assessment" ||
  round.interviewRoundId === "overall-assessment" ||
  round._id === "overall-assessment";

const normalizeOverallAssessmentRound = (
  round: InterviewRoundState,
): InterviewRoundState => {
  if (!isOverallAssessmentRound(round)) {
    return round;
  }

  const normalizedPrompt =
    round.questionGenerationPrompt?.trim() ||
    round.description?.trim() ||
    OVERALL_ASSESSMENT_DEFAULT_PROMPT;

  return {
    ...round,
    title: round.title || "Overall Assessment",
    description: round.description || OVERALL_ASSESSMENT_DEFAULT_PROMPT,
    questionGenerationPrompt: normalizedPrompt,
    answerType: round.answerType || "mixed",
    isMustQualify: round.isMustQualify ?? true,
    passPercentage: round.passPercentage ?? DEFAULT_PASS_PERCENTAGE,
  };
};

/**
 * Ensure default rounds exist (Profile Matching, Overall Assessment)
 *
 * In edit mode, only show existing rounds - don't auto-add defaults.
 * In new mode, ensures Profile Matching is always first and conditionally adds Overall Assessment.
 *
 * @param existingRounds - Current rounds to process
 * @param shouldAutoAddOverallAssessment - Whether to auto-add Overall Assessment (only during initial setup)
 * @param isEdit - Whether we're in edit mode
 * @returns Normalized array of rounds with Profile Matching first and proper numbering
 */
export const ensureDefaultRounds = (
  existingRounds: InterviewRoundState[],
  shouldAutoAddOverallAssessment: boolean = false,
  isEdit: boolean = false,
): InterviewRoundState[] => {
  // Filter out Job Alignment / Requirements rounds (they belong to Step 4, not Step 3)
  const filteredRounds = existingRounds.filter((round) => {
    const id = round.interviewRoundId || round._id || "";
    const title = round.title?.toLowerCase() || "";
    const isJobAlignment =
      id === "job-alignment" ||
      title === "job alignment" ||
      title === "requirements" ||
      (round as any).category?.some(
        (cat: string) =>
          cat.toLowerCase().replace(/\s+/g, "") === "jobalignment",
      );
    return !isJobAlignment;
  });
  const normalizedRounds = filteredRounds.map(normalizeOverallAssessmentRound);

  // In edit mode, ensure Profile Matching exists, but don't auto-add Overall Assessment
  if (isEdit) {
    // Find Profile Matching in filtered rounds
    const profileMatching = normalizedRounds.find(
      (round) =>
        round.title?.toLowerCase() === "profile matching" ||
        round.interviewRoundId === "profile-match" ||
        round._id === "profile-match",
    );

    // Get other rounds (excluding Profile Matching)
    const otherRounds = normalizedRounds.filter((round) => {
      const isProfileMatching =
        round.title?.toLowerCase() === "profile matching" ||
        round.interviewRoundId === "profile-match" ||
        round._id === "profile-match";
      return !isProfileMatching;
    });

    // Sort other rounds by round number
    const sortedOtherRounds = [...otherRounds].sort(
      (a, b) => (a.interviewRoundNumber || 0) - (b.interviewRoundNumber || 0),
    );

    // Build final array: Profile Matching first, then other rounds
    const allRounds: InterviewRoundState[] = [];

    // Always include Profile Matching at position 1
    if (profileMatching) {
      allRounds.push({
        ...profileMatching,
        interviewRoundNumber: 1,
        isMustQualify: profileMatching.isMustQualify ?? true,
      });
    } else {
      // If Profile Matching doesn't exist, add it (shouldn't happen, but safety check)
      // Profile Matching is hardcoded (not in DB), so use fallback description
      allRounds.push({
        interviewRoundId: "profile-match",
        interviewRoundNumber: 1,
        isMustQualify: true,
        title: "Profile Matching",
        description:
          "This stage is intended to validate the candidate's suitability for the specified job role.",
        questionGenerationPrompt: "",
        evaluationPrompt: "",
      });
    }

    // Add other rounds after Profile Matching
    sortedOtherRounds.forEach((round) => {
      allRounds.push({
        ...round,
        interviewRoundNumber: allRounds.length + 1,
      });
    });

    // Final re-numbering to ensure sequential order
    return allRounds.map((round, index) => ({
      ...round,
      interviewRoundNumber: index + 1,
    }));
  }

  // Find default rounds by ID/title
  const profileMatching = normalizedRounds.find(
    (round) =>
      round.title?.toLowerCase() === "profile matching" ||
      round.interviewRoundId === "profile-match" ||
      round._id === "profile-match",
  );

  const overallAssessment = normalizedRounds.find(
    (round) =>
      round.title?.toLowerCase() === "overall assessment" ||
      round.interviewRoundId === "overall-assessment" ||
      round._id === "overall-assessment",
  );

  // Get draggable rounds (custom rounds + Overall Assessment)
  const draggableRounds = normalizedRounds.filter((round) => {
    const isProfileMatching =
      round.title?.toLowerCase() === "profile matching" ||
      round.interviewRoundId === "profile-match" ||
      round._id === "profile-match";
    return !isProfileMatching;
  });

  // Check if there are any custom rounds (non-default rounds)
  const hasCustomRounds = draggableRounds.some(
    (r) =>
      r.title?.toLowerCase() !== "overall assessment" &&
      r.interviewRoundId !== "overall-assessment" &&
      r._id !== "overall-assessment",
  );

  const allRounds: InterviewRoundState[] = [];

  // 1. Profile Matching - ALWAYS Round 1 (fixed, not draggable)
  if (profileMatching) {
    allRounds.push({
      ...profileMatching,
      interviewRoundNumber: 1,
      isMustQualify: profileMatching.isMustQualify ?? true,
    });
  } else {
    // Profile Matching is hardcoded (not in DB), so keep fallback description
    allRounds.push({
      interviewRoundId: "profile-match",
      interviewRoundNumber: 1,
      isMustQualify: true,
      title: "Profile Matching",
      description:
        "This stage is intended to validate the candidate's suitability for the specified job role.",
      questionGenerationPrompt: "",
      evaluationPrompt: "",
    });
  }

  // 2. Draggable rounds (Overall Assessment and Custom rounds)
  const sortedDraggableRounds = [...draggableRounds].sort(
    (a, b) => (a.interviewRoundNumber || 0) - (b.interviewRoundNumber || 0),
  );

  // Add Overall Assessment only if:
  // 1. It doesn't exist
  // 2. We're in initial setup mode (shouldAutoAddOverallAssessment is true)
  // 3. AND there are no custom rounds (user hasn't added any custom rounds yet)
  // This prevents auto-adding Overall Assessment when user explicitly deletes it
  const hasOverallAssessment = sortedDraggableRounds.some(
    (r) =>
      r.title?.toLowerCase() === "overall assessment" ||
      r.interviewRoundId === "overall-assessment" ||
      r._id === "overall-assessment",
  );

  if (
    !hasOverallAssessment &&
    !overallAssessment &&
    shouldAutoAddOverallAssessment &&
    !hasCustomRounds
  ) {
    sortedDraggableRounds.push({
      interviewRoundId: "overall-assessment",
      interviewRoundNumber: allRounds.length + 1,
      isMustQualify: true,
      passPercentage: DEFAULT_PASS_PERCENTAGE,
      title: "Overall Assessment",
      description: OVERALL_ASSESSMENT_DEFAULT_PROMPT,
      questionGenerationPrompt: OVERALL_ASSESSMENT_DEFAULT_PROMPT,
      evaluationPrompt: "",
      answerType: "mixed",
    });
  }

  // Add all draggable rounds
  sortedDraggableRounds.forEach((round) => {
    allRounds.push({
      ...round,
      interviewRoundNumber: allRounds.length + 1,
    });
  });

  // Final re-numbering to ensure sequential order
  return allRounds.map((round, index) =>
    normalizeOverallAssessmentRound({
      ...round,
      interviewRoundNumber: index + 1,
    }),
  );
};
